import React from "react";
import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { useOutletContext } from "react-router-dom";
import { notifyError } from "../components/notify";
import OrderList from "./Order_List";
import {
  BASE_MOBILE_API_URL,
  BASIC_AUTH,
  formatDateForAPI,
  formatMonthDate,
} from "../constants";
import axios from "axios";
import TableLoader from "../components/Spinner/TableLoader";
import DateInput from "../components/DatePicker";

const OrderHistory = () => {
  const currentDate = new Date();

  const customerStr = localStorage.getItem("customerInfo");
  const customer = customerStr ? JSON.parse(customerStr) : null;

  const { setPageTitle } = useOutletContext();
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setPageTitle("Order History");
  }, [setPageTitle]);

  const [ordersData, setOrderData] = useState({
    order_header: [],
    order_details: [],
    order_payment_trsc: [],
  });
  const [filteredOrders, setFilteredOrders] = useState({
    order_header: [],
    order_details: [],
    order_payment_trsc: [],
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [fromDate, setFromDate] = useState(currentDate);
  const [toDate, setToDate] = useState(currentDate);
  const [status, setStatus] = useState("A");

  const parseEntryDate = (dateStr) => {
    if (!dateStr) return null;
    const isoAttempt = new Date(dateStr);
    if (!isNaN(isoAttempt)) return isoAttempt;
    const parts = dateStr.split(/[-/]/);
    if (parts.length === 3 && parts[0].length === 2) {
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    return null;
  };

  const applyStatusFilter = (data, statusValue, fromDate, toDate) => {
    let filteredHeaders = data.order_header;

    // STATUS FILTER
    if (statusValue === "A") {
      filteredHeaders = filteredHeaders.filter(
        (item) => item.status === "X" || item.status === "C",
      );
    } else {
      filteredHeaders = filteredHeaders.filter(
        (item) => item.status === statusValue,
      );
    }

    // DATE FILTER (NOW CORRECT ✅)
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0); // ✅ strip time
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);

      filteredHeaders = filteredHeaders.filter((item) => {
        const entryDate = parseEntryDate(item.entrydate); // ✅ safe parse
        if (!entryDate) return false;
        return entryDate >= from && entryDate <= to;
      });
    }

    setFilteredOrders({
      ...data,
      order_header: filteredHeaders,
    });
  };

  const handleProceed = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${BASE_MOBILE_API_URL}/get_customer_orderv1`,
        {
          params: {
            companycode: customer?.companycode,
            supervisorcode: customer?.customercode,
            branchcode: customer?.branchcode,
            fromdate: formatDateForAPI(fromDate),
            todate: formatDateForAPI(toDate),
            orderflag: "C",
            callfrom: "C",
            qr_code_token_val: "0",
          },
          headers: {
            Authorization: BASIC_AUTH,
          },
        },
      );

      const result = response.data;
      setOrderData(result);
      applyStatusFilter(result, status, fromDate, toDate);
    } catch (err) {
      if (err.response) {
        // Server responded with error status
        const errorMessage =
          err.response?.data && typeof err.response.data === "string"
            ? err.response.data
            : err.response?.data?.message ||
              err.response?.data?.error ||
              "Login failed";

        notifyError(errorMessage);
        setFilteredOrders({
          order_header: [],
          order_details: [],
          order_payment_trsc: [],
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const totalPcs = selectedOrder?.details?.reduce(
    (sum, item) => sum + Number(item.noofitem),
    0,
  );

  const totalQty = selectedOrder?.details?.reduce(
    (sum, item) => sum + Number(item.qty),
    0,
  );

  const totalAmountDetails = selectedOrder?.details?.reduce(
    (sum, item) => sum + Number(item.amount),
    0,
  );

  const itemMaster = React.useMemo(() => {
    const stored = localStorage.getItem("itemMaster");
    return stored ? JSON.parse(stored) : [];
  }, []);

  const itemMap = React.useMemo(() => {
    const map = {};
    itemMaster.forEach((item) => {
      map[item.itemcode] = item.name;
    });
    return map;
  }, [itemMaster]);

  return (
    <>
      <div className="d-flex flex-column align-items-center mt-2">
        {/* FILTER BAR */}
        <div className="w-100 px-2">
          <div className="row g-2 align-items-end">
            {/* From Date */}
            <div className="col-12 col-md-6 col-lg-4">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label text-white mb-0 text-nowrap col-5 col-lg-auto">
                  Select From Date :
                </label>
                <div className="flex-grow-1">
                  <DateInput
                    selected={fromDate}
                    onChange={setFromDate}
                    defaultMonth={fromDate || new Date()}
                  />
                </div>
              </div>
            </div>

            {/* To Date */}
            <div className="col-12 col-md-6 col-lg-4">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label text-white mb-0 text-nowrap col-5 col-lg-auto">
                  Select To Date :
                </label>
                <div className="flex-grow-1">
                  <DateInput
                    selected={toDate}
                    onChange={setToDate}
                    defaultMonth={toDate || new Date()}
                  />
                </div>
              </div>
            </div>

            {/* Button */}
            <div className="col-8 col-md-4 col-lg-2 ms-md-4 ms-lg-0 ms-5">
              <button className="btn btn-theme w-100" onClick={handleProceed}>
                Proceed
              </button>
            </div>
          </div>
        </div>
        <hr className="text-white w-100" />
        {ordersData && ordersData.order_header.length > 0 && (
          <div className="col-12 col-md-6 col-lg-3">
            <div className="d-flex align-items-center ">
              <label className="form-label text-white mb-0 text-nowrap col-3 col-lg-4">
                Status :
              </label>
              <select
                className="form-select flex-grow-1"
                value={status}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  setStatus(newStatus);
                  applyStatusFilter(ordersData, newStatus, fromDate, toDate);
                }}
              >
                <option value="A">All</option>
                <option value="C">Completed</option>
                <option value="X">Cancelled</option>
              </select>
            </div>
          </div>
        )}
        {loading ? (
          <TableLoader
            message={`Loading order data...`}
            size="sm"
            messageClass="tablemessage-white py-3"
            spinnerClass="tablespinner-white"
          />
        ) : filteredOrders && filteredOrders.order_header.length === 0 ? (
          <div className="text-center text-white py-4">No orders found.</div>
        ) : (
          <OrderList
            ordersData={filteredOrders}
            loading={loading}
            setSelectedOrder={setSelectedOrder}
            setShowModal={setShowModal}
            notifyError={notifyError}
            refreshOrders={handleProceed}
          />
        )}
      </div>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
        backdrop="static"
        keyboard={false}
      >
        <div className="bg-Modal-black">
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">Order Detail</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {selectedOrder && (
              <>
                {/* Order Info */}
                <div className="row mb-3 fw-bold">
                  <div className="col-12 d-block d-md-flex d-lg-flex justify-content-md-between justify-content-lg-between">
                    <div>Order No : {selectedOrder.header.ordercode}</div>
                    <div className="mb-1">
                      Date : {formatMonthDate(selectedOrder.header.entrydate)} |{" "}
                      {selectedOrder.header.entrytime}
                    </div>
                  </div>

                  <div className="col-md-12  d-flex  justify-content-end">
                    <div>
                      <span className="bg-white text-success px-2 py-1 mt-1 rounded">
                        Total Amount : {selectedOrder.header.totalamount}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <div className="table-container">
                  <div className="table-responsive">
                    <table className="table table-dark table-bordered table-striped table-sm">
                      <thead>
                        <tr>
                          <th className="text-center">Sr No.</th>
                          <th>Item</th>
                          <th className="text-center">Pcs</th>
                          <th className="text-center">Qty</th>
                          <th>Rate</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.details?.map((itm, i) => (
                          <tr key={i}>
                            <td className="text-center">{i + 1}</td>
                            <td>{itemMap[itm.itemcode] || ""}</td>
                            <td className="text-center">{itm.noofitem}</td>
                            <td className="text-center">{itm.qty}</td>
                            <td>{itm.rate}</td>
                            <td>{itm.amount}</td>
                          </tr>
                        ))}

                        <tr className="table-secondary fw-bold">
                          <td className="text-center">TOTAL</td>
                          <td></td>
                          <td className="text-center">{totalPcs}</td>
                          <td className="text-center">{totalQty}</td>
                          <td></td>
                          <td>{totalAmountDetails?.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  );
};
export default OrderHistory;
